import { PUBLICATION_FORMATS, SERIES_STATUSES, type SeriesListItem } from '@panelvault/shared';
import { BookMarked, DownloadCloud, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { EmptyState } from '../components/EmptyState';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select } from '../components/ui/controls';
import { Input } from '../components/ui/input';
import { Panel } from '../components/ui/panel';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { SeriesFormDialog } from '../features/catalog/SeriesFormDialog';
import { ImportSeriesDialog } from '../features/sources/ImportSeriesDialog';
import {
  usePrefetchSeries,
  usePublishers,
  useSeriesList,
  type SeriesListParams,
} from '../features/catalog/queries';
import { coverGradient } from '../lib/cover';
import { seriesCoverTransition } from '../lib/transitions';
import { imageUrl } from '../lib/api';

function SeriesCard({ series }: { series: SeriesListItem }) {
  const { t } = useTranslation();
  const prefetch = usePrefetchSeries();
  const cover = imageUrl(series.displayCoverImageId, 'medium');
  const meta = [series.publisherName, series.volume, series.startYear?.toString()]
    .filter(Boolean)
    .join(' · ');

  return (
    <Link
      to={`/series/${series.id}`}
      viewTransition
      className="group block"
      onPointerEnter={() => prefetch(series.id)}
      onFocus={() => prefetch(series.id)}
    >
      <Panel className="flex h-full gap-4 p-4 transition-colors group-hover:border-accent">
        <div
          className="aspect-[2/3] w-20 shrink-0 overflow-hidden rounded-lg border bg-surface-raised"
          style={{
            ...seriesCoverTransition(series.id),
            ...(cover ? {} : { backgroundImage: coverGradient(series.title) }),
          }}
        >
          {cover ? (
            <img src={cover} alt="" loading="lazy" className="size-full object-cover" />
          ) : null}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div>
            <h2 className="truncate text-lg text-foreground">{series.title}</h2>
            {meta ? <p className="truncate text-xs text-muted">{meta}</p> : null}
          </div>

          <div className="flex flex-wrap gap-1.5">
            <Badge variant={series.status === 'ongoing' ? 'accent' : 'neutral'}>
              {t(`status.${series.status}`)}
            </Badge>
            <Badge variant="outline">{t(`format.${series.format}`)}</Badge>
          </div>

          <Progress
            className="mt-auto"
            value={series.ownedCount}
            max={series.plannedCount}
            label={t('series.progress', {
              owned: series.ownedCount,
              total: series.plannedCount,
            })}
          />
        </div>
      </Panel>
    </Link>
  );
}

export function SeriesListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const publishers = usePublishers();

  const [params, setParams] = useState<SeriesListParams>({ page: 1, perPage: 24, sort: 'title' });
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const seriesList = useSeriesList(params);
  const items = seriesList.data?.items ?? [];
  const total = seriesList.data?.total ?? 0;
  const page = params.page ?? 1;
  const pageCount = Math.max(1, Math.ceil(total / (params.perPage ?? 24)));

  const update = (patch: SeriesListParams) =>
    setParams((current) => ({ ...current, ...patch, page: patch.page ?? 1 }));

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl text-foreground">{t('nav.series')}</h1>
          <p className="mt-1 text-sm text-muted">{t('series.count', { count: total })}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="surface" onClick={() => setImportOpen(true)}>
            <DownloadCloud className="size-4" />
            {t('import.open')}
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            {t('series.create')}
          </Button>
        </div>
      </header>

      <Panel className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Input
          placeholder={t('series.search')}
          value={params.q ?? ''}
          onChange={(event) => update({ q: event.target.value })}
        />

        <Select
          value={params.publisherId ?? ''}
          onChange={(event) => update({ publisherId: event.target.value })}
          aria-label={t('series.publisher')}
        >
          <option value="">{t('filter.allPublishers')}</option>
          {publishers.data?.map((publisher) => (
            <option key={publisher.id} value={publisher.id}>
              {publisher.name}
            </option>
          ))}
        </Select>

        <Select
          value={params.status ?? ''}
          onChange={(event) => update({ status: event.target.value as SeriesListParams['status'] })}
          aria-label={t('series.status')}
        >
          <option value="">{t('filter.allStatuses')}</option>
          {SERIES_STATUSES.map((status) => (
            <option key={status} value={status}>
              {t(`status.${status}`)}
            </option>
          ))}
        </Select>

        <Select
          value={params.format ?? ''}
          onChange={(event) => update({ format: event.target.value as SeriesListParams['format'] })}
          aria-label={t('series.format')}
        >
          <option value="">{t('filter.allFormats')}</option>
          {PUBLICATION_FORMATS.map((format) => (
            <option key={format} value={format}>
              {t(`format.${format}`)}
            </option>
          ))}
        </Select>

        <Select
          className="sm:col-span-2 lg:col-span-1"
          value={params.sort ?? 'title'}
          onChange={(event) => update({ sort: event.target.value as SeriesListParams['sort'] })}
          aria-label={t('filter.sort')}
        >
          <option value="title">{t('filter.sortTitle')}</option>
          <option value="progress">{t('filter.sortProgress')}</option>
          <option value="startYear">{t('filter.sortYear')}</option>
          <option value="createdAt">{t('filter.sortAdded')}</option>
        </Select>
      </Panel>

      {seriesList.isPending ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-36" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={BookMarked}
          title={t('series.emptyTitle')}
          description={t('series.emptyBody')}
          action={
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              {t('series.create')}
            </Button>
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((series) => (
            <SeriesCard key={series.id} series={series} />
          ))}
        </div>
      )}

      {pageCount > 1 ? (
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="surface"
            size="sm"
            disabled={page <= 1}
            onClick={() => update({ page: page - 1 })}
          >
            {t('pagination.previous')}
          </Button>
          <span className="text-sm text-muted">{t('pagination.page', { page, pageCount })}</span>
          <Button
            variant="surface"
            size="sm"
            disabled={page >= pageCount}
            onClick={() => update({ page: page + 1 })}
          >
            {t('pagination.next')}
          </Button>
        </div>
      ) : null}

      <ImportSeriesDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={(seriesId) => void navigate(`/series/${seriesId}`)}
      />

      <SeriesFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={(series) => void navigate(`/series/${series.id}`)}
      />
    </div>
  );
}
